/**
 * 統一APIクライアント
 * - エラーハンドリングの統一
 * - 認証トークン管理の統一
 * - レスポンス変換の統一
 * - ログ出力の統一
 */

import { getApiBaseUrl } from '../../config/urls';

const API_BASE_URL = getApiBaseUrl();

/**
 * API エラークラス
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public originalError?: Error
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * ネットワークエラークラス
 */
export class NetworkError extends Error {
  constructor(originalError: Error) {
    super('ネットワークエラーが発生しました');
    this.name = 'NetworkError';
    this.stack = originalError.stack;
  }
}

/**
 * API リクエスト設定
 */
interface ApiRequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  requireAuth?: boolean;
  timeout?: number;
}

/**
 * 統一されたAPIクライアント
 */
class UnifiedApiClient {
  private readonly baseURL: string;
  private readonly defaultTimeout: number = 30000; // 30秒

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * 認証トークンを取得
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * 認証トークンを削除してログアウト処理
   */
  private handleAuthError(): void {
    console.warn('認証エラーが発生しました。ログアウトします。');
    localStorage.removeItem('token');
    // リダイレクト処理は呼び出し元で制御
    window.dispatchEvent(new CustomEvent('auth-error'));
  }

  /**
   * HTTPヘッダーを構築
   */
  private buildHeaders(config: ApiRequestConfig): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...config.headers,
    };

    // 認証が必要な場合はトークンを追加
    if (config.requireAuth !== false) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * リクエストボディを準備
   */
  private prepareBody(body: any): string | FormData | null {
    if (!body) return null;
    
    // FormDataの場合はそのまま返す
    if (body instanceof FormData) {
      return body;
    }
    
    // オブジェクトの場合はJSON文字列に変換
    if (typeof body === 'object') {
      return JSON.stringify(body);
    }
    
    return String(body);
  }

  /**
   * APIリクエストを実行
   */
  async request<T>(endpoint: string, config: ApiRequestConfig = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.buildHeaders(config);
    const body = this.prepareBody(config.body);

    // FormDataの場合はContent-Typeを削除（ブラウザが自動設定）
    if (body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const fetchConfig: RequestInit = {
      method: config.method || 'GET',
      headers,
      body,
      credentials: 'include', // Cookie送信を有効化
    };


    try {
      // タイムアウト制御
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout || this.defaultTimeout);

      const response = await fetch(url, {
        ...fetchConfig,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // レスポンス処理
      if (!response.ok) {
        if (response.status === 401) {
          this.handleAuthError();
        }
        
        let errorMessage = response.statusText;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // JSON解析に失敗した場合はstatusTextを使用
        }

        throw new ApiError(response.status, errorMessage);
      }

      // 204 No Contentの場合は空のレスポンスを返す
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();
      

      return data;

    } catch (error) {
      console.error('❌ API Error:', {
        url,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(408, 'Request Timeout');
      }

      throw new NetworkError(error as Error);
    }
  }

  /**
   * GETリクエスト
   */
  async get<T>(endpoint: string, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POSTリクエスト
   */
  async post<T>(endpoint: string, body?: any, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  /**
   * PUTリクエスト
   */
  async put<T>(endpoint: string, body?: any, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  /**
   * DELETEリクエスト
   */
  async delete<T>(endpoint: string, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * PATCHリクエスト
   */
  async patch<T>(endpoint: string, body?: any, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }
}

// シングルトンインスタンス
export const apiClient = new UnifiedApiClient(API_BASE_URL);

// デフォルトエクスポート（既存コードとの互換性のため）
export default apiClient;

/**
 * 認証エラーイベントリスナーを設定するヘルパー関数
 */
export const setupAuthErrorHandler = (redirectCallback: () => void) => {
  window.addEventListener('auth-error', redirectCallback);
  return () => window.removeEventListener('auth-error', redirectCallback);
};